<template>
  <div class="data-works-layout">
    <!-- 左侧导航栏 -->
    <div class="left-navigation">
      <!-- 搜索框 -->
      <el-input v-model="searchQuery" placeholder="搜索SQL模板..." size="small" clearable class="search-input" />
      <!-- SQL模板菜单 -->
      <el-menu v-if="filteredSqlTemplates && filteredSqlTemplates.length > 0" default-active="" class="sql-template-menu" :unique-opened="true">
        <el-menu-item v-for="(item, index) in filteredSqlTemplates" :key="index" @click="applyTemplate(item.sql)" :class="{ 'active-template': currentSql === item.sql }">
          <span class="template-name">{{ item.name }}</span>
        </el-menu-item>
      </el-menu>
      <div v-else-if="searchQuery" class="no-results">未找到匹配的模板</div>
      <slot name="navigation"></slot>
    </div>

    <!-- 右侧主区域 -->
    <div class="right-main">
      <!-- 上部分: SQL编辑器 -->
      <div class="sql-section" :style="{ height: sqlEditorHeight + 'px' }">
        <div class="sql-header">
          <h3>SQL编辑器</h3>
          <div class="execution-control">
            <el-button type="success" @click="directExportSql" :loading="executing" :disabled="!editorSql.trim()" size="small">
              下载SQL
            </el-button>
            <el-button type="primary" @click="executeSql" :loading="executing" v-if="!autoExecute || !initialSql" size="small">
              执行SQL
            </el-button>
          </div>
        </div>
        <sql-editor v-model="editorSql" :readonly="false" :height="sqlEditorHeight" class="sql-editor" />
      </div>

      <!-- 拖动分隔条 -->
      <div class="drag-handle" @mousedown="startDrag" :class="{ dragging: isDragging }"></div>

      <!-- 下部分: 结果区域 -->
      <div class="result-section">
        <div class="result-controls">
          <!-- 结果选项卡 (固定在上方) -->
          <el-tabs v-model="activeTab" type="card" closable @tab-remove="removeTab" class="result-tabs">
            <el-tab-pane v-for="tab in resultTabs" :key="tab.name" :label="tab.name" :name="tab.name" />
          </el-tabs>

          <!-- 导出操作 (固定在右侧) -->
          <div class="export-actions">
            <el-button-group>
              <el-button type="success" @click="exportCurrentPage(activeTab)" :loading="resultTabs.find(t => t.name === activeTab)?.exportingCurrent || false" :disabled="!activeTab" size="small">
                导出当前页
              </el-button>
              <el-button type="warning" @click="exportAllData(activeTab)" :loading="resultTabs.find(t => t.name === activeTab)?.exportingAll || false" :disabled="!activeTab" size="small">
                导出全部数据
              </el-button>
            </el-button-group>
          </div>
        </div>

        <!-- 筛选控制和数据总条数显示 -->
        <div class="filter-controls">
          <!-- <el-button size="small" @click="clearAllFilters" :disabled="!hasActiveFilters">重置筛选</el-button> -->
          <span class="result-summary">
            总计{{ filteredDataCount }}条数据
          </span>
        </div>

        <!-- 结果内容区域 -->
        <div class="result-content">
          <vxe-table v-if="activeData.length" ref="xTable" :data="activeData" border show-overflow="title" :row-config="{ isHover: true }" :height="'100%'" :column-config="{ resizable: true }" :scroll-y="{ enabled: true, gt: 50 }" :loading="executing" :sort-config="{ remote: false, trigger: 'cell' }" :filter-config="filterConfig" @filter-change="handleFilterChange">
            <vxe-column v-for="col in columns" :key="col" :field="col" :title="col" width="200" show-overflow="title" align="center" sortable :filters="cachedFilters[col]" :filter-method="filterMethod">
              <template #default="{ row }">
                <div class="cell-content" @click="showCellContent(row[col])" :title="String(row[col])">
                  {{ row[col] }}
                </div>
              </template>
            </vxe-column>
          </vxe-table>

          <div v-else class="empty-result">暂无查询结果</div>
        </div>

        <!-- 单元格内容弹窗 -->
        <el-dialog v-model="cellContentDialogVisible" title="单元格内容" width="600px" @close="closeCellContentDialog" append-to-body>
          <pre class="cell-content-dialog-content">{{ currentCellContent }}</pre>
          <template #footer>
            <span class="dialog-footer">
              <el-button @click="closeCellContentDialog">关闭</el-button>
            </span>
          </template>
        </el-dialog>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted, onBeforeUnmount } from 'vue';
import { ElMessage } from 'element-plus';
import http from '@/utils/http';
import SqlEditor from './SqlEditor.vue';
import * as XLSX from 'xlsx';
const filterConfig = {
  remote: false,
  immediate: true,     // 勾选立即筛选
  showFooter: false,   // 隐藏底部按钮
  checkStrictly: true  // 勾选后不关闭面板
}

// SQL编辑器高度相关
const sqlEditorHeight = ref(window.innerHeight * 0.3); // 默认占30%
const isDragging = ref(false);
const startY = ref(0);
const startHeight = ref(0);

// 开始拖动
const startDrag = (e) => {
  isDragging.value = true;
  startY.value = e.clientY;
  startHeight.value = sqlEditorHeight.value;
  document.body.style.cursor = 'ns-resize';
  document.body.style.userSelect = 'none';
};

// 拖动中
const handleDrag = (e) => {
  if (!isDragging.value) return;

  const deltaY = e.clientY - startY.value;

  // 正向计算高度，让体验符合预期
  let newHeight = startHeight.value + deltaY;

  const minHeight = 50;
  const maxHeight = window.innerHeight * 0.7;

  if (newHeight < minHeight) newHeight = minHeight;
  if (newHeight > maxHeight) newHeight = maxHeight;

  sqlEditorHeight.value = newHeight;
};

// 结束拖动
const endDrag = () => {
  if (isDragging.value) {
    isDragging.value = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }
};

// 监听全局鼠标移动和释放事件
onMounted(() => {
  document.addEventListener('mousemove', handleDrag);
  document.addEventListener('mouseup', endDrag);
  document.addEventListener('keydown', handleEscKey);
  window.addEventListener('resize', updateDefaultHeight);
});

// 组件卸载时移除事件监听
onBeforeUnmount(() => {
  document.removeEventListener('mousemove', handleDrag);
  document.removeEventListener('mouseup', endDrag);
  document.removeEventListener('keydown', handleEscKey);
  window.removeEventListener('resize', updateDefaultHeight);
});
const updateDefaultHeight = () => {
  if (!isDragging.value) {
    sqlEditorHeight.value = window.innerHeight * 0.3; // 自动适配
  }
};

const props = defineProps({
  sql: {
    type: String,
    default: ''
  },
  autoExecute: {
    type: Boolean,
    default: false
  },
  sqlTemplates: {
    type: Array,
    default: () => []
  }
});

const applyTemplate = (sql) => {
  editorSql.value = sql;
  if (props.autoExecute) {
    executeSql();
  }
};

// 执行SQL函数定义
const executeSql = async () => {
  if (!editorSql.value.trim()) {
    ElMessage.warning('SQL不能为空');
    return;
  }
  if (!editorSql.value.endsWith(';')) {
    editorSql.value += ';'
  }

  try {
    executing.value = true;
    const result = await http.executeDataWorksSql(editorSql.value);

    if (result?.success) {
      const tabName = `执行结果${tabIndex.value++}`;
      resultTabs.value.push({
        name: tabName,
        data: result.data,
        sql: editorSql.value, // 保存当前的 SQL 查询语句
        exportingCurrent: false,
        exportingAll: false
      });
      activeTab.value = tabName;
    } else {
      ElMessage.error(result?.errorMsg || '执行SQL失败');
    }
  } catch (error) {
    console.error('执行SQL失败:', error);
    ElMessage.error('执行SQL失败');
  } finally {
    executing.value = false;
  }
};

// 直接导出SQL数据 - 防抖实现
const directExportDebounceTimer = ref(null);
const directExportSql = async () => {
  // 防重复点击：如果正在执行中，直接返回
  if (executing.value) {
    return;
  }

  // 防抖处理
  if (directExportDebounceTimer.value) {
    clearTimeout(directExportDebounceTimer.value);
  }

  directExportDebounceTimer.value = setTimeout(async () => {
    if (!editorSql.value.trim()) {
      ElMessage.warning('SQL不能为空');
      return;
    }

    try {
      executing.value = true;

      // 获取当前时间用于文件名
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      const fileName = `导出SQL数据_${year}${month}${day}${hours}${minutes}${seconds}.xlsx`;

      // 调用后端导出接口，使用当前编辑器中的SQL
      const response = await http.exportDataWorksSqlResult(editorSql.value);
      if (response?.success) {
        // 创建一个隐藏的a标签来触发下载，并设置文件名
        const link = document.createElement('a');
        link.href = response.data;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        ElMessage.success('SQL数据导出成功');
      } else {
        ElMessage.error(response?.errorMsg || '导出SQL数据失败');
      }
    } catch (error) {
      console.error('导出SQL数据失败:', error);
      ElMessage.error('导出SQL数据时出错');
    } finally {
      executing.value = false;
    }
  }, 300); // 300ms防抖延迟
};

const editorSql = ref('');
const executing = ref(false);
const activeTab = ref('');
const resultTabs = ref([]);
const tabIndex = ref(1);
const initialSql = computed(() => props.sql.trim());

// 活跃数据计算属性
const activeData = computed(() => {
  const tab = resultTabs.value.find(t => t.name === activeTab.value)
  return tab?.data || []
})

// 提前一次性计算 column keys
const columns = computed(() => {
  return activeData.value.length ? Object.keys(activeData.value[0]) : []
})

// 缓存 filters，避免反复计算
const cachedFilters = computed(() => {
  const map = {}
  const data = activeData.value

  if (data.length === 0) return map

  columns.value.forEach(col => {
    const unique = new Set()
    data.forEach(row => unique.add(row[col]))

    map[col] = [...unique].slice(0, 50).map(v => ({
      label: String(v),
      value: v
    }))
  })

  return map
})

// 搜索相关
const searchQuery = ref('');
const currentSql = ref('');

// 过滤后的SQL模板
const filteredSqlTemplates = computed(() => {
  if (!searchQuery.value) {
    return props.sqlTemplates;
  }
  const query = searchQuery.value.toLowerCase();
  return props.sqlTemplates.filter(template =>
    template.name.toLowerCase().includes(query) ||
    template.sql.toLowerCase().includes(query)
  );
});

// 监听editorSql变化来更新当前SQL
watch(editorSql, (newVal) => {
  currentSql.value = newVal;
});

// 单元格内容弹窗相关
const cellContentDialogVisible = ref(false);
const currentCellContent = ref('');

// 显示单元格内容
const showCellContent = (content) => {
  currentCellContent.value = String(content || '');
  cellContentDialogVisible.value = true;
};

// 关闭弹窗
const closeCellContentDialog = () => {
  cellContentDialogVisible.value = false;
  currentCellContent.value = '';
};

// ESC键关闭弹窗
const handleEscKey = (event) => {
  if (event.key === 'Escape' && cellContentDialogVisible.value) {
    closeCellContentDialog();
  }
};

// 监听ESC键
onMounted(() => {
  document.addEventListener('keydown', handleEscKey);
});

// 组件卸载时移除事件监听
onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleEscKey);
});

// 初始化SQL编辑器内容
watch(() => props.sql, (newVal) => {
  editorSql.value = newVal;
  if (props.autoExecute && newVal.trim()) {
    executeSql();
  }
}, { immediate: true });

// 关闭选项卡
const removeTab = (name) => {
  const tabs = resultTabs.value;
  let activeName = activeTab.value;

  if (activeName === name) {
    const index = tabs.findIndex(tab => tab.name === name);
    activeName = tabs[index + 1]?.name || tabs[index - 1]?.name || '';
  }

  resultTabs.value = tabs.filter(tab => tab.name !== name);
  activeTab.value = activeName;
};

// 导出当前页

const exportCurrentPage = async (tabName) => {
  const tab = resultTabs.value.find(t => t.name === tabName);
  if (!tab || !tab.data?.length) {
    ElMessage.warning('没有可导出的数据');
    return;
  }

  try {
    tab.exportingCurrent = true;

    // 创建workbook
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(tab.data);

    // 添加worksheet到workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    // 导出Excel文件
    XLSX.writeFile(workbook, `${tab.name}.xlsx`);

    ElMessage.success(`${tab.name} - 当前页数据导出成功`);
  } catch (error) {
    console.error('导出当前页失败:', error);
    ElMessage.error('导出当前页失败');
  } finally {
    tab.exportingCurrent = false;
  }
};

// 导出全部数据
const exportAllData = async (tabName) => {
  const tab = resultTabs.value.find(t => t.name === tabName);
  if (!tab) {
    ElMessage.warning('请先选择要导出的结果');
    return;
  }

  try {
    tab.exportingAll = true;

    // 使用保存的 SQL 查询语句
    const sqlToExport = tab.sql;

    // 调用后端导出接口
    const response = await http.exportDataWorksSqlResult(sqlToExport);
    if (response?.success) {
      window.open(response.data, '_blank');
      ElMessage.success('全部数据导出任务已提交');
    } else {
      ElMessage.error(response?.errorMsg || '导出全部数据失败');
    }
  } catch (error) {
    console.error('导出全部数据失败:', error);
    ElMessage.error('导出全部数据时出错');
  } finally {
    tab.exportingAll = false;
  }
};

// 筛选方法
const filterMethod = ({ value, row, column }) => {
  const cellValue = String(row[column.property] || '');
  return cellValue.includes(String(value));
};

// 表格引用
const xTable = ref(null);

// 处理筛选变化
const handleFilterChange = ({ column, values, $table }) => {
  // immediate: true 已经确保了筛选会立即应用
  // 这里可以添加其他逻辑，比如更新UI等
};

// 重置所有筛选
const clearAllFilters = () => {
  if (xTable.value) {
    xTable.value.clearFilter();
  }
};

// 是否有活跃的筛选
const hasActiveFilters = computed(() => {
  if (!xTable.value) return false;
  try {
    const filterArgs = xTable.value.getColumnFilterArgs();
    return filterArgs && Object.keys(filterArgs).some(key =>
      filterArgs[key] && filterArgs[key].values && filterArgs[key].values.length > 0
    );
  } catch (error) {
    return false;
  }
});

// 筛选后的数据条数
const filteredDataCount = computed(() => {
  if (!xTable.value) return activeData.value.length;
  const tableData = xTable.value.getTableData();
  return tableData.visibleData ? tableData.visibleData.length : activeData.value.length;
});

// 移除了 getCellStyle 函数，直接在模板中使用内联样式
</script>

<style scoped>
.data-works-layout {
  display: flex;
  height: 100vh;
  width: 100%;
  background: #f5f7fa;
}

.data-works-layout .left-navigation {
  width: 200px;
  background: #fff;
  border-right: 1px solid #e6e6e6;
  padding: 0 10px;
  overflow-y: auto;
}

.data-works-layout .left-navigation .search-input {
  margin-bottom: 10px;
}

.data-works-layout .left-navigation .sql-template-menu {
  border-right: none;
}

.data-works-layout .left-navigation .sql-template-menu .el-menu-item {
  height: 36px;
  line-height: 36px;
  padding: 0 10px;
  margin: 2px 0;
  border-radius: 4px;
  overflow: hidden;
  transition: background-color 0.2s ease;
}

.data-works-layout
  .left-navigation
  .sql-template-menu
  .el-menu-item.active-template {
  background-color: #e6f7ff;
  color: #1890ff;
  font-weight: bold;
}

.data-works-layout .left-navigation .no-results {
  padding: 0 10px;
  color: #999;
  font-size: 12px;
  text-align: center;
}

.data-works-layout .right-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
}

.data-works-layout .sql-section {
  display: flex;
  flex-direction: column;
  padding: 0 15px;
  background: #fff;
  border-bottom: none;
  min-height: 0;
}

.data-works-layout .drag-handle {
  height: 4px;
  background: #e6e6e6;
  cursor: ns-resize;
  transition: background-color 0.2s ease;
  z-index: 10;
}

.data-works-layout .drag-handle:hover,
.data-works-layout .drag-handle.dragging {
  background: #1890ff;
}

.data-works-layout .result-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #fff;
  overflow: hidden;
  border-top: 1px solid #e6e6e6;
  padding: 0;
  min-height: 0;
}

.data-works-layout .sql-section .sql-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.data-works-layout .sql-section .execution-control {
  display: flex;
  gap: 8px;
  align-items: center;
}

.data-works-layout .sql-section .sql-editor {
  flex: 1;
}

.data-works-layout .result-section .result-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 10px 10px;
  border-bottom: 1px solid #e6e6e6;
}

.data-works-layout .result-section .result-tabs {
  flex: 1;
  min-width: 0;
}

.data-works-layout .result-section .export-actions {
  margin-left: 10px;
}

.data-works-layout .result-section .filter-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  background: #f9f9f9;
  border-bottom: 1px solid #e6e6e6;
  margin-bottom: 10px;
}

.data-works-layout .result-section .filter-controls .result-summary {
  font-size: 14px;
  color: #666;
  margin: 0;
}

.data-works-layout .result-section .result-content {
  flex: 1;
  overflow: hidden;
  padding: 0;
}

/* 移除了可能导致ResizeObserver问题的复杂表格样式 */

.data-works-layout .result-section .empty-result {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #999;
}

/* 单元格内容样式 */
.data-works-layout .result-section .cell-content {
  width: 100%;
  height: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  padding: 4px 8px;
  line-height: 1.4;
}

/* 列头样式 */
.column-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

/* 弹窗内容样式 */
.cell-content-dialog-content {
  max-height: 400px;
  overflow-y: auto;
  padding: 16px;
  background: #f9f9f9;
  border-radius: 4px;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: monospace;
  font-size: 14px;
  line-height: 1.5;
}
</style>
